import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { V3_APP_BASE_URL } from '@/constants/Config';

interface ICollegiateData {
  id: number;
  name: string;
  for_iterable?: string;
}

interface IErrRes {
  status: string | number;
  data: { message: string };
}

/**  Endpoint to fetch schools.  */
export const fetchSchoolsApi = createApi({
  reducerPath: 'fetchSchoolsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  endpoints: (builder) => ({
    getSchools: builder.query({
      query: (params) => ({
        url: `api/v4/school`,
        params,
        method: 'get',
      }),
      transformResponse: (response: { schools: ICollegiateData[] }) =>
        response.schools.map((school) => ({
          label: school.name,
          value: school.name,
          id: school.id,
          for_iterable: school.for_iterable,
        })),
      transformErrorResponse: (response: { error: IErrRes }) => response,
      serializeQueryArgs: ({ endpointName }) => endpointName,
      /** Always merge incoming data to the cache entry */
      merge: (currentCache, newItems) => {
        currentCache.push(...newItems);
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getSchool: builder.query({
      query: (params) => ({
        url: `api/v4/school`,
        params,
        method: 'get',
      }),
      transformResponse: (response: { schools: ICollegiateData[] }) => response.schools,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
  }),
});

/** Endpoint to fetch organizations.  */
export const fetchOrganizationsApi = createApi({
  reducerPath: 'fetchOrganizationsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: V3_APP_BASE_URL,
  }),
  endpoints: (builder) => ({
    getOrganizations: builder.query({
      query: (params) => ({
        url: `api/v4/organization`,
        params,
        method: 'get',
      }),
      transformResponse: (response: { organizations: ICollegiateData[] }) =>
        response.organizations.map((organization) => ({
          label: organization.name,
          value: organization.name,
          id: organization.id,
        })),
      transformErrorResponse: (response: { error: IErrRes }) => response,
      serializeQueryArgs: ({ endpointName }) => endpointName,
      /** Always merge incoming data to the cache entry */
      merge: (currentCache, newItems) => {
        currentCache.push(...newItems);
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getOrganization: builder.query({
      query: (params) => ({
        url: `api/v4/organization`,
        params,
        method: 'get',
      }),
      transformResponse: (response: { organizations: ICollegiateData[] }) => response.organizations,
      transformErrorResponse: (response: { error: IErrRes }) => response,
    }),
  }),
});

/** Export hooks for usage in functional components, which are auto-generated based on the defined endpoints */
export const { useGetSchoolsQuery, useLazyGetSchoolQuery } = fetchSchoolsApi;
export const { useGetOrganizationsQuery, useLazyGetOrganizationQuery } = fetchOrganizationsApi;
